import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/site/Reveal";
import EditorialMarquee from "@/components/site/Marquee";
import ParallaxImage from "@/components/site/ParallaxImage";
import VisitorCounter from "@/components/site/VisitorCounter";
import { useContent } from "@/context/ContentContext";

export default function Home() {
  const { content } = useContent();
  const H = content.home;
  const brand = content.brand || {};
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const featured = (content.courses || []).slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen flex-col justify-end overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt="Neon optical fibers"
            className="h-full w-full object-cover opacity-30 mix-blend-luminosity"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(60% 55% at 15% 20%, rgba(0,102,255,0.55) 0%, transparent 60%), radial-gradient(55% 60% at 85% 85%, rgba(255,0,127,0.45) 0%, transparent 60%)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/75 to-navy/40" />
        </motion.div>
        {/* floating brand orbs */}
        <div className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-brand-blue/30 blur-[120px]" />
        <div className="pointer-events-none absolute -right-32 bottom-10 h-[28rem] w-[28rem] rounded-full bg-brand-magenta/25 blur-[130px]" />

        <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-[1400px] px-6 pb-20 pt-40 lg:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 flex items-center gap-3 font-mono text-sm uppercase tracking-[0.3em] text-brand-blue"
          >
            <span className="h-1.5 w-1.5 bg-brand-magenta" /> {H.heroLabel}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8 }}
            data-testid="hero-company-name"
            className="mb-4 font-display text-2xl font-semibold tracking-tight text-brand-blue sm:text-3xl"
          >
            {brand.companyName}
          </motion.h2>

          <h1 className="font-display text-[13vw] font-medium leading-[0.85] tracking-tight text-white sm:text-[11vw] lg:text-[8.5vw]">
            <MaskedLines lines={[H.heroLine1, H.heroLine2]} delay={0.15} />
            <MaskedLines
              lines={[H.heroLine3]}
              delay={0.4}
              lineClassName="text-brand-gradient"
            />
          </h1>

          <div className="mt-12 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="max-w-md text-lg font-light leading-relaxed text-slate-300"
            >
              {H.heroSubtext}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="flex items-center gap-4"
            >
              <Link
                to="/courses"
                data-testid="hero-courses-cta"
                className="brand-glow-blue group flex items-center gap-2 bg-brand-blue px-7 py-3.5 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta"
              >
                Explore Courses
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/contact"
                className="link-underline px-2 py-3.5 font-mono text-sm uppercase tracking-[0.2em] text-brand-magenta"
              >
                Book a call
              </Link>
            </motion.div>
          </div>

          <div className="mt-14">
            <VisitorCounter />
          </div>
        </motion.div>

        {!reduce && (
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="relative mx-auto mb-6 flex w-full max-w-[1400px] justify-center px-6 text-slate-500 lg:px-12"
          >
            <ArrowDown size={18} />
          </motion.div>
        )}
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 bg-surface">
        <EditorialMarquee items={H.marquee} />
      </div>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-6 py-28 lg:px-12 lg:py-36" data-testid="manifesto-section">
        <Reveal>
          <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand-blue">{H.manifestoLabel}</p>
          <h2 className="mt-6 max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {H.manifestoPre}{" "}
            <span className="text-brand-gradient">{H.manifestoHighlight}</span>
          </h2>
        </Reveal>

        <div className="mt-20 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {(H.chapters || []).map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1} className="h-full">
              <div className="group relative h-full overflow-hidden bg-navy p-10 transition-colors duration-500 hover:bg-surface">
                <span className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-brand-blue to-brand-magenta transition-transform duration-500 group-hover:scale-x-100" />
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl font-semibold text-brand-magenta">{c.n}</span>
                  <span className="h-2 w-2 rotate-45 bg-brand-blue/40 transition-colors duration-300 group-hover:bg-brand-blue" />
                </div>
                <h3 className="mt-8 font-display text-2xl font-medium text-white">{c.title}</h3>
                <p className="mt-4 text-lg font-light leading-relaxed text-slate-400">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED COURSES */}
      <section className="border-t border-white/10 bg-surface py-28 lg:py-36" data-testid="featured-courses">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand-blue">Curriculum</p>
              <h2 className="mt-6 font-display text-4xl font-medium tracking-tight text-white sm:text-5xl">
                Featured programmes
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/courses" className="link-underline font-mono text-sm uppercase tracking-[0.2em] text-slate-300">
                View all courses →
              </Link>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
            {featured.map((c, i) => (
              <Reveal key={c.id} delay={(i % 2) * 0.08}>
                <Link
                  to="/courses"
                  data-testid={`home-course-${c.id}`}
                  className="group relative flex h-full flex-col justify-between gap-10 overflow-hidden bg-navy p-10 transition-colors duration-500 hover:bg-surface-2"
                >
                  <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-brand-blue to-brand-magenta opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="flex items-center justify-between">
                    <span className="border border-brand-blue/40 bg-brand-blue/10 px-2.5 py-1 font-mono text-sm uppercase tracking-[0.2em] text-brand-blue">{c.code}</span>
                    <ArrowUpRight
                      size={20}
                      className="text-slate-600 transition-all duration-300 group-hover:text-brand-magenta group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </div>
                  <div>
                    <h3 className="font-display text-3xl font-medium text-white">{c.title}</h3>
                    <p className="mt-3 max-w-md text-lg font-light leading-relaxed text-slate-400">{c.blurb}</p>
                    <div className="mt-6 flex flex-wrap gap-x-6 gap-y-1 font-mono text-base uppercase tracking-[0.15em] text-slate-400">
                      <span>{c.level}</span>
                      <span>{c.duration}</span>
                      <span>{c.format}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-32">
        <ParallaxImage
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Data analytics"
          className="absolute inset-0 opacity-15"
        />
        <div className="absolute inset-0 bg-navy/70" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(50% 50% at 30% 50%, rgba(0,102,255,0.35) 0%, transparent 60%), radial-gradient(45% 50% at 75% 50%, rgba(255,0,127,0.3) 0%, transparent 60%)",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-6 text-center lg:px-12">
          <Reveal>
            <h2 className="mx-auto max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl">
              {H.ctaPre} <span className="text-brand-gradient">{H.ctaHighlight}</span>
            </h2>
            <Link
              to="/contact"
              data-testid="cta-enquire"
              className="brand-glow-magenta group mt-10 inline-flex items-center gap-2 bg-brand-magenta px-8 py-4 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-blue"
            >
              Enquire about a cohort
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
